import React, { useEffect, useRef } from "react";
import { FiX, FiMessageCircle } from "react-icons/fi";

/**
 * DetailPanel - V4-style slide-in detail panel
 *
 * @param {boolean} isOpen - Whether the panel is open
 * @param {function} onClose - Callback when panel is closed
 * @param {string} title - Panel title
 * @param {string} subtitle - Panel subtitle
 * @param {array} tabs - Tab definitions [{id, label}]
 * @param {string} activeTab - Currently active tab id
 * @param {function} onTabChange - Callback when tab changes
 * @param {node} children - Main content area
 * @param {node} sidebar - Sidebar content (optional)
 * @param {node} footer - Footer content (optional)
 * @param {node} headerActions - Additional header action buttons
 * @param {function} onOpenChat - Callback to open Lazlo chat (optional)
 */
const DetailPanel = ({
	isOpen,
	onClose,
	title,
	subtitle,
	tabs = [],
	activeTab,
	onTabChange,
	children,
	sidebar,
	footer,
	headerActions,
	onOpenChat,
}) => {
	const panelRef = useRef(null);

	// Close on escape key
	useEffect(() => {
		const handleEscape = (e) => {
			if (e.key === "Escape" && isOpen) {
				onClose();
			}
		};
		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [isOpen, onClose]);

	// Prevent body scroll when panel is open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => {
			document.body.style.overflow = "";
		};
	}, [isOpen]);

	return (
		<>
			{/* Backdrop */}
			<div
				className={`detail-panel-backdrop ${isOpen ? "detail-panel-backdrop--visible" : ""}`}
				onClick={onClose}
			/>

			{/* Panel */}
			<aside
				ref={panelRef}
				className={`detail-panel ${isOpen ? "detail-panel--open" : ""}`}
			>
				{/* Header */}
				<header className="detail-panel__header">
					<div className="detail-panel__header-content">
						<h2 className="detail-panel__title">{title}</h2>
						{subtitle && <span className="detail-panel__subtitle">{subtitle}</span>}
					</div>
					<div className="detail-panel__header-actions">
						{headerActions}
						{onOpenChat && (
							<button
								className="detail-panel__chat-btn"
								onClick={onOpenChat}
								title="Open Lazlo"
							>
								<FiMessageCircle />
							</button>
						)}
						<button className="detail-panel__close" onClick={onClose}>
							<FiX />
						</button>
					</div>
				</header>

				{/* Tabs */}
				{tabs.length > 0 && (
					<div className="detail-panel__tabs">
						{tabs.map((tab) => (
							<button
								key={tab.id}
								className={`detail-panel__tab ${activeTab === tab.id ? "detail-panel__tab--active" : ""}`}
								onClick={() => onTabChange(tab.id)}
							>
								{tab.label}
							</button>
						))}
					</div>
				)}

				{/* Body */}
				<div className={`detail-panel__body ${!sidebar ? "detail-panel__body--full" : ""}`}>
					<div className="detail-panel__content">
						{children}
					</div>
					{sidebar && (
						<aside className="detail-panel__sidebar">
							{sidebar}
						</aside>
					)}
				</div>

				{/* Footer */}
				{footer && (
					<footer className="detail-panel__footer">
						{footer}
					</footer>
				)}
			</aside>
		</>
	);
};

/**
 * PanelSection - Section within detail panel content
 */
export const PanelSection = ({ title, children }) => (
	<section className="panel-section">
		{title && <h3 className="panel-section__title">{title}</h3>}
		{children}
	</section>
);

/**
 * PanelRow - Key-value row within a section
 */
export const PanelRow = ({ label, value, children }) => (
	<div className="panel-section__row">
		<span className="panel-section__label">{label}</span>
		<span className="panel-section__value">{children || value}</span>
	</div>
);

/**
 * SummaryCard - Summary card for sidebar
 */
export const SummaryCard = ({ label, children }) => (
	<div className="summary-card">
		<div className="summary-card__header">
			<span className="summary-card__label">{label}</span>
		</div>
		<div className="summary-card__rows">
			{children}
		</div>
	</div>
);

/**
 * SummaryRow - Row within summary card
 */
export const SummaryRow = ({ label, value, variant }) => (
	<div className={`summary-card__row ${variant ? `summary-card__row--${variant}` : ""}`}>
		<span className="summary-card__row-label">{label}</span>
		<span className="summary-card__row-value">{value}</span>
	</div>
);

export default DetailPanel;
